import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import { fetchWithCsrf } from "@/lib/api";
import { CURRENT_USER_QUERY_KEY } from "@/hooks/api/useCurrentUser";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import type { User } from "@/types/auth";
import type { ApiSuccess, ApiError } from "@/types/api";

interface UpdateProfileData {
  name?: string;
  email?: string;
  password?: string;
  passwordConfirmation?: string;
}

export default function Settings() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const [name, setName] = useState(user?.name || "");
  const [email, setEmail] = useState(user?.email || "");
  const [password, setPassword] = useState("");
  const [passwordConfirmation, setPasswordConfirmation] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const updateProfile = useMutation<
    ApiSuccess<{ user: User }>,
    ApiError,
    UpdateProfileData
  >({
    mutationFn: async (data) => {
      return fetchWithCsrf("/api/current_user", {
        method: "PATCH",
        body: JSON.stringify({ user: data }),
      });
    },
    onSuccess: (response) => {
      queryClient.setQueryData(CURRENT_USER_QUERY_KEY, response.data.user);
      setPassword("");
      setPasswordConfirmation("");
      setSuccessMessage("Profile updated successfully");
      setTimeout(() => setSuccessMessage(""), 3000);
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const data: UpdateProfileData = { name, email };
    if (password) {
      data.password = password;
      data.passwordConfirmation = passwordConfirmation;
    }
    updateProfile.mutate(data);
  };

  const fieldErrors = updateProfile.error?.errors || {};

  return (
    <div className="max-w-2xl">
      <h1 className="text-xl font-medium uppercase tracking-wider mb-8">Settings</h1>

      {successMessage && (
        <div className="bg-primary/15 text-primary px-4 py-3 rounded-lg text-sm mb-6">
          {successMessage}
        </div>
      )}

      {updateProfile.isError && updateProfile.error?.message && (
        <div className="bg-destructive/10 border border-destructive/20 rounded-lg px-4 py-3 text-sm text-destructive mb-6">
          {updateProfile.error.message}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="space-y-2">
          <Label htmlFor="name">Name</Label>
          <Input
            id="name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
          />
          {fieldErrors.name && (
            <p className="text-sm text-destructive">{fieldErrors.name[0]}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="email">Email</Label>
          <Input
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
          {fieldErrors.email && (
            <p className="text-sm text-destructive">{fieldErrors.email[0]}</p>
          )}
        </div>

        <div className="border-t pt-6">
          <h2 className="text-sm font-medium uppercase tracking-wider mb-4">Change Password</h2>
          <p className="text-sm text-muted-foreground mb-4">
            Leave blank to keep your current password
          </p>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="password">New Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="new-password"
                placeholder="At least 8 characters"
              />
              {fieldErrors.password && (
                <p className="text-sm text-destructive">
                  {fieldErrors.password[0]}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="passwordConfirmation">Confirm New Password</Label>
              <Input
                id="passwordConfirmation"
                type="password"
                value={passwordConfirmation}
                onChange={(e) => setPasswordConfirmation(e.target.value)}
                autoComplete="new-password"
              />
              {fieldErrors.passwordConfirmation && (
                <p className="text-sm text-destructive">
                  {fieldErrors.passwordConfirmation[0]}
                </p>
              )}
            </div>
          </div>
        </div>

        <Button type="submit" disabled={updateProfile.isPending}>
          {updateProfile.isPending ? "Saving..." : "Save Changes"}
        </Button>
      </form>
    </div>
  );
}
